import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { TrendingDown, Siren, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MULTIPLIERS = [1.0, 1.5, 2.0];

export default function AtrPanel({ anyAuthenticated }) {
  const [multiplier, setMultiplier] = useState("2.0");
  const [running, setRunning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [schedule, setSchedule] = useState("09:20");

  const loadSchedule = useCallback(async () => {
    try {
      const res = await api.get("/atr-sl/schedule");
      setAutoEnabled(res.data.enabled === true);
      setSchedule(res.data.schedule || "09:20");
      setMultiplier(String(res.data.atr_multiplier || 2.0));
    } catch { /* ok */ }
  }, []);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const run = async () => {
    if (!anyAuthenticated) {
      toast.error("Connect at least one broker first");
      return;
    }
    setRunning(true);
    try {
      const res = await api.post("/atr-sl/run", { atr_multiplier: parseFloat(multiplier) });
      toast.success(`ATR ${multiplier}x SL run completed — ${res.data?.summary?.orders_placed || 0} order(s) placed`);
      setConfirming(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "ATR SL run failed");
    } finally {
      setRunning(false);
    }
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      await api.post("/atr-sl/schedule", {
        enabled: autoEnabled,
        schedule,
        atr_multiplier: parseFloat(multiplier),
      });
      toast.success(autoEnabled ? `ATR auto-scheduled daily at ${schedule} IST` : "Auto-schedule disabled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-surface-2 border-border rounded-sm h-full">
      <CardHeader className="pb-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1 font-semibold">
          / command
        </div>
        <CardTitle className="text-lg font-medium flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-profit" />
          ATR Trailing SL
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Places trailing stop-loss at ATR(14) × multiplier below the highest
          price since entry. Supports 1.0x, 1.5x, and 2.0x multipliers.
        </p>

        {/* Multiplier selector */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
            Multiplier
          </span>
          <Select value={multiplier} onValueChange={setMultiplier}>
            <SelectTrigger className="h-8 rounded-sm bg-surface-1 border-border text-xs w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MULTIPLIERS.map((m) => (
                <SelectItem key={m} value={String(m)}>
                  {m}x
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Auto-schedule */}
        <div className="flex items-center gap-3">
          <Clock className="w-3 h-3 text-muted-foreground" />
          <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
            Auto daily at
          </span>
          <Input
            type="time"
            value={schedule}
            onChange={e => setSchedule(e.target.value)}
            className="h-8 rounded-sm bg-surface-1 border-border text-xs w-28 font-mono"
          />
          <div className="flex items-center gap-2">
            <Label htmlFor="atr-auto-toggle" className="text-[10px] text-muted-foreground">On</Label>
            <Switch
              id="atr-auto-toggle"
              checked={autoEnabled}
              onCheckedChange={setAutoEnabled}
              className="scale-75"
            />
          </div>
          <Button
            onClick={saveSchedule}
            disabled={saving}
            variant="outline"
            className="h-8 rounded-sm text-[10px] px-2"
          >
            {saving ? "..." : "Save"}
          </Button>
        </div>

        {/* Run button */}
        {!confirming ? (
          <Button
            onClick={() => setConfirming(true)}
            disabled={!anyAuthenticated || running}
            className="w-full h-10 rounded-sm bg-profit text-white hover:bg-profit/90 font-medium text-xs uppercase tracking-[0.12em]"
          >
            <Siren className="w-3.5 h-3.5 mr-2" />
            Run ATR {multiplier}x SL Now
          </Button>
        ) : (
          <div className="space-y-2 border border-profit/50 bg-profit/5 p-3 rounded-sm">
            <div className="text-xs text-profit font-mono uppercase tracking-[0.12em]">
              confirm live action
            </div>
            <p className="text-xs text-muted-foreground">
              This places REAL SL orders on your connected broker accounts at ATR({multiplier}x) levels.
            </p>
            <div className="flex gap-2 pt-1">
              <Button
                onClick={run}
                disabled={running}
                className="flex-1 h-9 rounded-sm bg-profit text-white hover:bg-profit/90 text-xs font-medium"
              >
                {running ? "Running..." : "Yes, execute"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setConfirming(false)}
                className="h-9 rounded-sm text-xs"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
